import type { ChainFamily, PayeeRow, ShareValidation } from './types';

export function bpsToPercentLabel(bps: bigint): string {
  return `${(Number(bps) / 100).toFixed(2)}%`;
}

export function parseFeeBps(input: string): bigint | null {
  const t = input.trim();
  if (!/^\d+$/.test(t)) return null;
  try {
    return BigInt(t);
  } catch {
    return null;
  }
}

export function isValidAddress(addr: string, family: ChainFamily): boolean {
  const trimmed = addr.trim();
  if (!trimmed) return false;
  switch (family) {
    case 'evm':
      return /^0x[0-9a-fA-F]{40}$/.test(trimmed);
    case 'solana':
      return /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(trimmed);
    case 'sui':
      return /^0x[0-9a-fA-F]{64}$/.test(trimmed);
    default:
      return false;
  }
}

export function addressPlaceholder(family: ChainFamily): string {
  switch (family) {
    case 'evm':
      return '0x…';
    case 'solana':
      return 'Base58 pubkey…';
    case 'sui':
      return '0x… (64 hex)';
  }
}

export function validateShares(rows: PayeeRow[], family: ChainFamily): ShareValidation {
  let sumComplete = BigInt(0);
  let sumDisplay = BigInt(0);
  let completeCount = 0;
  let incomplete = false;

  for (const r of rows) {
    const a = r.address.trim();
    const s = r.shares.trim();
    if (!a && !s) continue;

    const hasA = Boolean(a);
    const hasS = Boolean(s);
    if (hasA !== hasS) {
      incomplete = true;
      if (hasS && /^\d+$/.test(s)) {
        sumDisplay += BigInt(s);
      }
      continue;
    }

    if (!/^\d+$/.test(s)) {
      incomplete = true;
      continue;
    }
    const shareN = BigInt(s);

    if (!isValidAddress(a, family)) {
      incomplete = true;
      sumDisplay += shareN;
      continue;
    }

    completeCount++;
    sumComplete += shareN;
    sumDisplay += shareN;
  }

  return {
    shareSum: sumDisplay,
    shareSumValid: !incomplete && completeCount > 0 && sumComplete === BigInt(100),
    includedPayeeCount: completeCount,
    shareRowsIncomplete: incomplete,
  };
}

export function defaultRows(): PayeeRow[] {
  return [
    { address: '', shares: '50' },
    { address: '', shares: '50' },
  ];
}

export function emptyRow(): PayeeRow {
  return { address: '', shares: '' };
}
